import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam, ChartRowDataArray } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";
import { createRowData } from "./common";
import { ChartDialogConfig } from "../../common/constants";

interface IGetModelEvaluationDataResponceModel {
  /**
   * 整定時間
   */
  settlingtime: number;
  /**
   * 立ち上がり時間
   */
  risetime: number;
  /**
   * オーバーシュート
   */
  overshoot: number;
  /**
   * オフセット
   */
  offset: number;
  /**
   * 安定性
   */
  stability: number;
  /**
   * 出力データのタグ名の配列
   */
  outputtags: string[];
  /**
   * プロセス値のタグ名の配列
   */
  pvtags: string[];
  /**
   * サンプリング周期[msec]
   */
  interval: number;
  /**
   * 出力データの配列（1行配列の配列）
   */
  outrows: ChartRowDataArray;
  /**
   * プロセスデータの配列（1行配列の配列）
   */
  pvrows: ChartRowDataArray;
}

function convertPvData(data: IGetModelEvaluationDataResponceModel): ChartRowDataArray {
  // rows
  const datalist: ChartRowDataArray = new Array(data.pvrows.length + 1);
  datalist[0] = ["x", ...data.pvtags];
  data.pvrows.forEach((row, i) => {
    const idx = i + 1;
    const xdata = i * data.interval / 1000;
    datalist[idx] = [xdata, ...row];
  });
  return datalist;
}

function convertOutData(data: IGetModelEvaluationDataResponceModel): ChartRowDataArray {
  // rows
  const datalist: ChartRowDataArray = new Array(data.outrows.length + 1);
  datalist[0] = ["x", ...data.outputtags];
  data.outrows.forEach((row, i) => {
    const idx = i + 1;
    const xdata = i * data.interval / 1000;
    datalist[idx] = [xdata, ...row];
  });
  return datalist;
}

function AxiosGet(url: string, token?: string) {
  if (token) {
    return axios.get(url, {
      headers: { Authorization: "Bearer " + token },
    });
  } else {
    return axios.get(url);
  }
}

export interface IModelEvaluationDataRow extends IGetModelEvaluationDataResponceModel {
  /**
   * id
   */
  id: number;
  /**
   * データ数
   */
  datanum: number;  
}

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIからシミュレータのシミュレーション結果の取得が成功した場合に呼び出されます
   * @callback success
   * @param {IModelEvaluationDataRow} row - シミュレーション結果一覧
   * @param {number} status - httpステータス
   */
  success: (row: IModelEvaluationDataRow, status: number) => void;
}

export interface GetModelEvaluationDataModel {
  /**
   * プロジェクトID
   */
  projectid: string;
  /**
   * データ間引き閾値
   */
  threshold: number;
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
  authService?: AuthService;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((value) => {      
      const data: IGetModelEvaluationDataResponceModel = value.data;
      const pvrows = convertPvData(data);
      const outrows = convertOutData(data);
      const threshold = ChartDialogConfig.TRIMMING_CHART_THRESHOLD;
      const datanum = pvrows.length - 1;
      const overshoot = Math.round(Number(data.overshoot) * Math.pow(10, 5)) / Math.pow(10, 5);
      const offset = Math.round(Number(data.offset) * Math.pow(10, 5)) / Math.pow(10, 5);
      const stability = Math.round(Number(data.stability) * Math.pow(10, 5)) / Math.pow(10, 5);
      const row: IModelEvaluationDataRow = {
        id: 0,
        datanum: datanum,
        settlingtime: data.settlingtime,
        risetime: data.risetime,
        overshoot: overshoot,
        offset: offset,
        stability: stability,
        outputtags: data.outputtags,
        pvtags: data.pvtags,
        interval: data.interval,
        outrows: createRowData(outrows, threshold, true),
        pvrows: createRowData(pvrows, threshold, true),
      }
      params.success(row, value.status);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface IGetModelEvaluationDataParams extends ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * プロジェクトID
   */
  projectid: string;
  /**
   * モデルID
   */
  modelid: string;
}

/**
 * モデル評価結果の内容を取得します
 * @function GetModelEvaluationData
 * @param {IGetModelEvaluationDataParams} params - モデル評価結果の内容取得パラメータ
 */
export function GetModelEvaluationData(params: IGetModelEvaluationDataParams): void {
  const { authService, projectid, modelid } = params;
  const url = WebAPI.GET_EVALUATION(projectid, modelid);
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosGet(url, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              getModelEvaluationDataRetry401(authService, url, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosGet(url),
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * モデル評価結果の内容を取得します
 * @function getModelEvaluationDataRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {ICallbackParams} params - コールバックパラメータ
 */
 function getModelEvaluationDataRetry401(
  authService: AuthService,
  url: string,
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosGet(url, token),
        success: params.success,
        fail: params.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}